import { FC, useState } from 'react';
import RestartIcon from '@mui/icons-material/RestartAlt';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import { ConfirmationModal } from 'entities/ConfirmationModal';
import { useTranslations } from 'shared/hooks/useTranslations';
import { useCloseModalWithDelay } from 'shared/helpers/useCloseModalWithDelay';
import { getDistributorId } from 'shared/helpers/getDistributorId';
import { Button } from 'entities/Button';
import { useStore } from 'effector-react';
import { globalStore } from 'shared/store';
import { DATE_FORMAT } from 'shared/globalConsts';
import { productionStore, updateOrders } from 'shared/store';
import { discardProduct } from 'shared/store';
import { getLayout } from 'shared/helpers/getLayout';
import { useGetSkuShippingPointNum } from 'shared/hooks/useGetSkuShippingPointNum';
import { AvailableStatus } from 'graphql/generatedModel';
import { handleLocalStorage } from 'shared/helpers/handleLocalStorage';
import { updateDisplay } from 'shared/api';
import { get } from 'http';

export const ResetButton: FC<IResetButton> = ({ closeMotherModal }) => {
  //
  // Hooks
  const t = useTranslations();
  const [progress, startClosing] = useCloseModalWithDelay(closeMotherModal, 2000);

  // State
  const date = useStore(globalStore).date.format(DATE_FORMAT);
  const { currentOpenProduct: product } = useStore(productionStore);

  // State
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isLastBatchResetModalOpen, setIsLastBatchResetModalOpen] = useState(false);

  const shippingPointName = handleLocalStorage('shippingPointName');

  const getSkuShippingNum = useGetSkuShippingPointNum();
  const startResettingProduct = async () => {
    startClosing();
    await discardProduct({
      params:
        product?.productionListOrders
          ?.filter((o1: any) => o1.productStatus) // no need to reset "to do" product
          ?.map((order: any) => {
            return {
              dateFor: date,
              productId: Number(product?.articleId),
              productName: `${product?.description}`,
              partNum: product?.deliveryCycleNum,
              orderId: Number(order?.orderId),
              productStatus: order.productStatus,
              responsibleId: getDistributorId(order?.responsibleId as number),
              customerNum: getSkuShippingNum(order.customerNum),
            };
          }) || [],
    });
    closeMotherModal();
  };

  const startResettingProductLastBatch = async () => {
    const productsToUpdate = [],
      deviceUpdateItems: {
        __typename?: 'ProductionListOrders';
        amount?: number | null;
        orderId?: string | null;
        deliveryNoteNum?: number | null;
        customerId?: string | null;
        customerNum?: string | null;
        internalName?: string | null;
        distributionLowerLimitInPercent?: number | null;
        distributionHigherLimitInPercent?: number | null;
        productStatus?: AvailableStatus | null;
        responsibleId?: number | null;
        distributed?: number | null;
        prevDistributed?: number | null;
        prevDistributorId?: number | null;
        toDistribute?: number | null;
        priority?: string | null;
      }[] = [];
    for (const order of product?.productionListOrders || []) {
      if (order?.prevDistributed) {
        productsToUpdate.push(order);
        if (order?.prevDistributorId && order?.productStatus !== AvailableStatus.PENDING) {
          deviceUpdateItems.push(order);
        }
      }
    }
    startClosing();
    if (productsToUpdate?.length) {
      await updateOrders({
        date,
        productId: Number(product?.articleId),
        productName: `${product?.description}`,
        shippingName: shippingPointName || '',
        fields: productsToUpdate.map((order: any) => {
          const nextDistributed = Math.max(
            0,
            Number(order?.distributed || 0) - Number(order?.prevDistributed || 0),
          );
          // console.log({
          //   nextDistributed,
          //   prevDistributed: order?.prevDistributed,
          //   distributed: order?.distributed,
          //   toDistribute: order?.toDistribute,
          // }); // --- IGNORE ---

          return {
            orderId: Number(order?.orderId),
            partNum: product?.deliveryCycleNum,
            distributed: nextDistributed,
            toDistribute: 0,
            responsibleId: nextDistributed > 0 ? order?.responsibleId : null,
            prevDistributed: null,
            // responsibleId:
            //   nextDistributed > 0 ? getDistributorId(order?.responsibleId as number) : null,
            productStatus: nextDistributed > 0 ? AvailableStatus.PENDING : AvailableStatus.TO_DO,
          };
        }),
      });
      await updateDisplay({
        date,
        productName: `${product?.description}`,
        productStatus: AvailableStatus.PENDING,
        shippingName: shippingPointName || '',
        customers: productsToUpdate.map((order: any) => ({
          sku: getSkuShippingNum(order.customerNum),
          customerId: Number(order?.customerId),
          distributorId: getDistributorId(order?.responsibleId as number),
          amount: 0,
        })),
      });

      // console.log({ deviceUpdateItems }); // --- IGNORE ---

      if (deviceUpdateItems?.length) {
        await updateDisplay({
          date,
          productName: `${product?.description}`,
          productStatus: AvailableStatus.PENDING,
          shippingName: shippingPointName || '',
          customers: deviceUpdateItems.map((order: any) => ({
            sku: getSkuShippingNum(order.customerNum),
            customerId: Number(order?.customerId),
            distributorId: getDistributorId(order?.prevDistributorId as number),
            amount: order?.prevDistributed || 0,
          })),
        });
      }
    }
    closeMotherModal();
  };

  const lastBatchResetBtnActive = product?.productionListOrders?.some((order) => {
    return order?.prevDistributed && order?.productStatus === AvailableStatus.PENDING;
  });

  return (
    <>
      <Button
        variant={getLayout() === 'DESKTOP' ? 'outlined' : 'text'}
        color="error"
        handleClick={() => {
          setIsResetModalOpen(true);
        }}
        height="56px"
        width="56px"
      >
        <RestartIcon />
      </Button>

      {lastBatchResetBtnActive && (
        <Button
          variant={getLayout() === 'DESKTOP' ? 'outlined' : 'text'}
          color="error"
          handleClick={() => {
            setIsLastBatchResetModalOpen(true);
          }}
          height="56px"
          width="56px"
        >
          <RotateLeftIcon />
        </Button>
      )}

      <ConfirmationModal
        isOpen={isResetModalOpen}
        progress={progress}
        headerTitle={t('common.areYouSure')}
        text={t('production.resetProgressQuestion')}
        onClose={() => setIsResetModalOpen(false)}
        onConfirm={startResettingProduct}
      />

      <ConfirmationModal
        isOpen={isLastBatchResetModalOpen}
        progress={progress}
        headerTitle={t('common.areYouSure')}
        text={t('production.resetLastBatchProgressQuestion')}
        onClose={() => setIsLastBatchResetModalOpen(false)}
        onConfirm={startResettingProductLastBatch}
      />
    </>
  );
};

interface IResetButton {
  closeMotherModal: () => void;
}
